// ── Contract Checklist ────────────────────────────────────────
// Live verification panel: each acceptance criterion from the intake
// Design Brief judged against the actual run output. Rendered above the
// output doc in WorkflowJourneyView when a contract exists.
//
// States (status prop):
//   verifying  — verifier call in flight (iteration N)
//   fixing     — targeted fix pass applying nodes
//   done       — final report (all pass, or capped with remaining gaps)
//   error      — verification failed (fail-open: informational only)
//
// See features/ai-workflows/contract-loop.feature.

import React, { useState } from 'react';

const VERDICT_ICON = { pass: '✓', partial: '◐', fail: '✗' };
const VERDICT_CLASS = { pass: 'wfs-cc-pass', partial: 'wfs-cc-partial', fail: 'wfs-cc-fail' };

export default function ContractChecklist({ status, criteria = [], iteration = 0, note, error }) {
  const [collapsed, setCollapsed] = useState(false);

  if (!criteria.length && status !== 'verifying' && status !== 'fixing') return null;

  const pass = criteria.filter(c => c.verdict === 'pass').length;
  const total = criteria.length;
  const allPass = total > 0 && pass === total;

  const headline =
    status === 'verifying' ? `Verifying against the brief${iteration > 0 ? ` (pass ${iteration + 1})` : ''}…`
    : status === 'fixing' ? 'Fixing unmet criteria…'
    : status === 'error' ? 'Verification unavailable'
    : allPass ? `All ${total} acceptance criteria met`
    : `${pass}/${total} acceptance criteria met`;

  return (
    <div className={`wfs-cc ${allPass && status === 'done' ? 'wfs-cc--allpass' : ''}`} role="region" aria-label="Contract verification">
      <button type="button" className="wfs-cc-header" onClick={() => setCollapsed(c => !c)}>
        <span className="wfs-cc-icon" aria-hidden="true">
          {status === 'verifying' || status === 'fixing'
            ? <span className="refine-pulse">●</span>
            : allPass ? '📜✓' : '📜'}
        </span>
        <span className="wfs-cc-headline">{headline}</span>
        {note && status === 'done' && <span className="wfs-cc-note">{note}</span>}
        <span className="wfs-cc-toggle">{collapsed ? '▸' : '▾'}</span>
      </button>

      {!collapsed && criteria.length > 0 && (
        <ul className="wfs-cc-list">
          {criteria.map((c, i) => (
            <li key={i} className={`wfs-cc-item ${VERDICT_CLASS[c.verdict] || ''}`}>
              <span className="wfs-cc-verdict" aria-label={c.verdict}>{VERDICT_ICON[c.verdict] || '○'}</span>
              <div className="wfs-cc-body">
                <div className="wfs-cc-criterion">{c.criterion}</div>
                {c.evidence && <div className="wfs-cc-evidence">{c.evidence}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}

      {status === 'error' && error && (
        <div className="wfs-cc-error">Verification failed: {error} — the run itself is unaffected.</div>
      )}
    </div>
  );
}
